import React, { createContext, useContext } from 'react';
import { FormControl, InputBase, Typography } from '@material-ui/core';
import { IOnboardingAccount, IOnboardingLaw, IOnboardingSetup, INamedEntity } from '../../../../../Api/AptorApi';
import { useStyles as useFieldStyles } from '../../../../../Components/Field/Field.styles';
import SelectField from '../../../../../Components/Field/SelectField';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';

export interface OnboardingDraft extends Omit<IOnboardingSetup, 'legislation' | 'notes'> {
  selectedLaws: Array<INamedEntity & { number: string }>;
  unitIds: number[];
  noteUnitIds: Record<number, number[]>;
  noteLawIds?: number[];
  noteCompanyUnitIds?: number[];
  noteText?: string;
  notes: Record<string, string>;
  unitName: string;
  requirement: IOnboardingSetup['requirements'][number];
}
export const emptyRequirement = (): OnboardingDraft['requirement'] => ({ name: '', area: 0, effectiveFrom: '', description: '', unitIds: [] });
export const OnboardingDraftContext = createContext<{
  draft: OnboardingDraft; update: (change: (current: OnboardingDraft) => OnboardingDraft) => void;
  account: IOnboardingAccount; languages: INamedEntity[]; areas: INamedEntity[];
}>(undefined as any);
export const useOnboardingDraft = () => useContext(OnboardingDraftContext);
export const noteKey = (lawId: number, unitId: number) => `${lawId}:${unitId}`;
export const hasText = (html: string) => !!html.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/g, ' ').trim();
export const lawRows = (draft: OnboardingDraft): IOnboardingLaw[] => draft.selectedLaws.flatMap(law =>
  draft.unitIds.map(unitId => ({ ...law, companyUnitId: unitId,
    companyUnitName: draft.units.find(unit => unit.id === unitId)?.name || '',
    note: draft.notes[noteKey(law.id, unitId)] || draft.notes[String(law.id)] || '',
  })),
);
export const validRequirement = (item: OnboardingDraft['requirement']) => !!item.name.trim() && item.name.length <= 255 && item.area > 0 && !!item.effectiveFrom && hasText(item.description) && item.description.length <= 2000 && item.unitIds.length > 0 && (!item.effectiveTo || item.effectiveTo >= item.effectiveFrom);
export const validStep = (step: number, draft: OnboardingDraft, _account: IOnboardingAccount) => {
  switch (step) {
    case 0: return draft.units.length > 0 && !draft.unitName.trim();
    case 6: return !!draft.admin.firstName.trim() && !!draft.admin.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.admin.email) && draft.admin.language > 0;
    case 1: return draft.selectedLaws.length > 0;
    case 3: {
      if ((draft.noteLawIds || []).length || (draft.noteCompanyUnitIds || []).length || hasText(draft.noteText || '')) return false;
      return Object.entries(draft.noteUnitIds).every(([lawId, unitIds]) => unitIds.every(unitId =>
        draft.unitIds.includes(unitId) && hasText(draft.notes[noteKey(Number(lawId), unitId)] || draft.notes[lawId] || ''),
      ));
    }
    case 4: return draft.requirements.every(item => validRequirement(item) && item.unitIds.every(id => draft.units.some(unit => unit.id === id)))
      && JSON.stringify(draft.requirement) === JSON.stringify(emptyRequirement());
    default: return true;
  }
};
export const setupPayload = (draft: OnboardingDraft): IOnboardingSetup => ({
  units: draft.units, admin: draft.admin,
  legislation: draft.selectedLaws.length ? [{ lawIds: draft.selectedLaws.map(law => law.id), unitIds: draft.units.map(unit => unit.id) }] : [],
  notes: draft.selectedLaws.flatMap(law => (draft.noteUnitIds[law.id] || []).map(unitId => ({
    lawId: law.id, unitId, note: draft.notes[noteKey(law.id, unitId)] || draft.notes[String(law.id)] || '',
  }))),
  requirements: draft.requirements,
});

// Controlled fields update the draft immediately; Next never reads a delayed text value.
export const OnboardingDraftText = ({ label, value, onChange, type = 'text', required = true }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean }) => {
  const classes = useFieldStyles();
  const id = React.useId();
  return <FormControl className={classes.formControl}>
    <Typography component="label" htmlFor={id}>{label}{required && '*'}</Typography>
    <InputBase id={id} type={type} value={value} onChange={event => onChange(event.target.value)} classes={{ focused: classes.focused, error: classes.error }} />
  </FormControl>;
};
export const OnboardingDraftSelect = ({ name, label, ids, options, onChange, multi = true, required = false }: {
  name: string; label: string; ids: number[]; options: INamedEntity[]; onChange: (ids: number[]) => void; multi?: boolean; required?: boolean;
}) => <SelectField key={name + ':' + ids.join(',')} name={name} inputId={name} label={label} isMulti={multi} required={required} errorTexts={[]}
  options={options.map(option => ({ value: option.id, label: option.name }))}
  initialState={multi ? ids.map(value => ({ value, label: options.find(option => option.id === value)?.name || String(value) })) : ids[0] ? { value: ids[0], label: options.find(option => option.id === ids[0])?.name || String(ids[0]) } : undefined}
  onChange={(value?: TypedFormFieldValue<number> | TypedFormFieldValue<number>[]) => onChange(Array.isArray(value) ? value.map(item => item.value) : value ? [value.value] : [])} />;
